import { defineStore } from "pinia";
import { ref } from "vue";
import type {
  ApiResult,
  UserCredentials,
  Profile,
  ForgotPassword,
  SignInResponse,
  RefreshTokenResponse,
} from "@/types/auth";
import type { CustomAxiosRequestConfig } from "@/plugins/axios";
import { authApi } from "@/plugins/axios";

export const useAuthStore = defineStore("auth", () => {
  const error = ref<Error | string | null>(null);
  const accessToken = ref<string | null>(null);
  const accessRefreshToken = ref<string | null>(null);
  const tokenExpiration = ref<string | null>(null);
  const isAuthenticated = ref<boolean>(false);

  const TOKEN_KEY = "gcr-access-token";
  const REFRESH_TOKEN_KEY = "gcr-refresh-token";
  const TOKEN_EXPIRY_KEY = "gcr-token-expiration";
  const USER_ID = "gcr-userId";
  const PROFILE = "gcr-profile";
  const AGENCY = "gcr-agency";

  const setToken = (signInData: SignInResponse) => {
    const userProfile: Profile = {
      fullName: signInData.user.fullName,
      role: signInData.user.role[0],
    };

    accessToken.value = signInData.tokens.accessToken;
    accessRefreshToken.value = signInData.tokens.refreshToken;
    tokenExpiration.value = signInData.tokens.accessTokenExpiration;
    if (accessToken.value) isAuthenticated.value = true;

    localStorage.setItem(TOKEN_KEY, signInData.tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, signInData.tokens.refreshToken);
    localStorage.setItem(
      TOKEN_EXPIRY_KEY,
      signInData.tokens.accessTokenExpiration
    );
    localStorage.setItem(USER_ID, JSON.stringify(true));
    localStorage.setItem(PROFILE, JSON.stringify(userProfile));
    localStorage.setItem(AGENCY, JSON.stringify(signInData.agency));
  };

  const updateToken = (refreshTokenData: RefreshTokenResponse) => {
    accessToken.value = refreshTokenData.accessToken;
    accessRefreshToken.value = refreshTokenData.refreshToken;
    tokenExpiration.value = refreshTokenData.accessTokenExpiration;
    if (accessToken.value) isAuthenticated.value = true;

    localStorage.setItem(TOKEN_KEY, refreshTokenData.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshTokenData.refreshToken);
    localStorage.setItem(
      TOKEN_EXPIRY_KEY,
      refreshTokenData.accessTokenExpiration
    );
  };

  const fetchAllTokens = () => {
    accessToken.value = localStorage.getItem(TOKEN_KEY);
    accessRefreshToken.value = localStorage.getItem(REFRESH_TOKEN_KEY);
    tokenExpiration.value = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (accessToken.value) isAuthenticated.value = true;
  };

  const clearToken = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(USER_ID);
    localStorage.removeItem(PROFILE);
    localStorage.removeItem(AGENCY);
    accessToken.value = null;
    isAuthenticated.value = false;
  };

  const getAccessToken = () => {
    return localStorage.getItem(TOKEN_KEY);
  };

  const getRefreshToken = () => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  };

  const getTokenExpiration = () => {
    return localStorage.getItem(TOKEN_EXPIRY_KEY);
  };

  const getUserProfile = () => {
    return localStorage.getItem(PROFILE);
  };

  const getUserAgency = () => {
    return localStorage.getItem(AGENCY);
  };

  const signIn = async (loginDetails: UserCredentials): Promise<ApiResult> => {
    try {
      error.value = null;
      const response = await authApi.post("/api/auth/login", loginDetails, {
        showLoader: false,
        skipAuthRedirect: true,
      } as CustomAxiosRequestConfig);

      if (response.status === 200) {
        setToken(response.data);

        return { success: true };
      }
      return { success: false };
    } catch {
      error.value = "Incorrect Email or Password";
      return { success: false };
    }
  };

  const getToken = async () => {
    const now = Math.floor(Date.now() / 1000);

    const tokenExpires = Math.floor(
      new Date(tokenExpiration.value!).getTime() / 1000
    );

    if (accessToken.value && now < tokenExpires) {
      return accessToken.value;
    }

    try {
      console.log("GOT TO REFRESH")
      const response = await refreshToken();

      if (!response.success) {
        return accessToken.value;
      }
    } catch (error) {
      console.log("Failed to refresh token", error);
    }

    return accessToken.value;
  };

  const refreshToken = async (): Promise<ApiResult> => {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    if (!accessToken || !refreshToken) {
      throw new Error("No tokens available");
    }
    try {
      error.value = null;
      const response = await authApi.post(
        "/api/auth/refresh",
        { accessToken, refreshToken },
        {
          showLoader: true,
          skipAuthRedirect: true,
        } as CustomAxiosRequestConfig
      );

      if (response.status === 200) {
        updateToken(response.data);
        return { success: true };
      }
      clearToken();
      return { success: false };
    } catch (err) {
      console.log("refresh-Token-error", err);
      clearToken();
      error.value = "Your account was accessed from another device. Only one active session is allowed at a time."
      return { success: false };
    }
  };

  const forgotPassword = async (email: ForgotPassword): Promise<ApiResult> => {
    try {
      error.value = null;
      const response = await authApi.put("/api/auth/forgotPassword", email, {
        showLoader: false,
        skipAuthRedirect: true,
      } as CustomAxiosRequestConfig);

      if (response.status === 204) {
        return { success: true };
      }
      error.value = "The Email provided is not registered.";
      return { success: false };
    } catch {
      error.value = "The Email provided is not registered.";
      return { success: false };
    }
  };

  const fetchDemoAPIKey = async (): Promise<string | null> => {
    try {
      error.value = null;
      const response = await authApi.get(`/api/agencies/APIkey`, {
        showLoader: true,
      } as CustomAxiosRequestConfig);

      if (response.status === 200) {
        return response.data.key;
      }
      return null;
    } catch (err) {
      error.value =
        err instanceof Error ? err : new Error("Failed to load API Key");
      console.log(err);
      return null;
    }
  };

  const isTokenExpired = () => {
    const expiration = getTokenExpiration();
    if (!expiration) return true;

    const expirationTime = new Date(expiration).getTime();
    const currentTime = Date.now();
    const bufferTime = 60000; // Refresh 1 minute before expiry

    return currentTime >= expirationTime - bufferTime;
  };


  // Logout
  const logout = () => {
    clearToken();
  };

  const isSuperAdmin = () => {
    const stored = getUserProfile();
    if (!stored) return false;
    const profile: Profile = JSON.parse(stored);
    return profile.role === "SuperAdmin";
  };

  return {
    error,
    isAuthenticated,
    getAccessToken,
    getRefreshToken,
    getTokenExpiration,
    isSuperAdmin,
    getUserAgency,
    getUserProfile,
    setToken,
    clearToken,
    signIn,
    forgotPassword,
    refreshToken,
    isTokenExpired,
    logout,
    getToken,
    fetchAllTokens,
    fetchDemoAPIKey
  };
});